/**
 * Authentication Context - Manages user authentication state
 */

import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { authAPI } from '../services/api';
import { getPortalMode } from '../config/portal';

const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Check if user is authenticated on mount
  useEffect(() => {
    checkAuth();
  }, []);

  const checkAuth = async () => {
    try {
      const response = await authAPI.getProfile();
      const nextUser = response.user;
      const portalMode = getPortalMode();
      const isAdminOrStaff = ['admin', 'staff'].includes(nextUser?.role);

      if ((portalMode === 'admin' && !isAdminOrStaff) || (portalMode === 'customer' && isAdminOrStaff)) {
        setUser(null);
      } else {
        setUser(nextUser);
      }
    } catch (err) {
      // No active authenticated session
      setUser(null);
    } finally {
      setLoading(false);
    }
  };

  const login = async (email, password, portal = 'customer') => {
    setError(null);
    try {
      const response = await authAPI.login({ email, password, portal });

      // Set user
      setUser(response.user);
      
      return { success: true, user: response.user };
    } catch (err) {
      setError(err.message);
      return { success: false, error: err.message };
    }
  };

  const register = async (userData) => {
    setError(null);
    try {
      const response = await authAPI.register(userData);

      // Set user
      setUser(response.user);
      
      return { success: true, user: response.user };
    } catch (err) {
      setError(err.message);
      return { success: false, error: err.message };
    }
  };

  const logout = useCallback(async () => {
    try {
      await authAPI.logout();
    } catch (err) {
      // Proceed with local logout even if network call fails.
    } finally {
      setUser(null);
      setError(null);
    }
  }, []);

  const updateUser = (updatedData) => {
    setUser(prev => ({ ...prev, ...updatedData }));
  };

  const clearError = () => setError(null);

  const value = {
    user,
    loading,
    error,
    isAuthenticated: !!user,
    isAdmin: user?.role === 'admin',
    isStaff: user?.role === 'staff' || user?.role === 'admin',
    login,
    register,
    logout,
    updateUser,
    clearError,
    checkAuth,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export default AuthContext;

